import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { NotificationKindEnum } from '@zaoblako/shared/modules/notification/enums/notification.kind.enum';
import { PersonalAccountMenuService } from '@zaoblako/web/core/services/lk/personal-account-menu.service';
import { NotificationService } from '@zaoblako/web/core/services/notification/notification.service';
import { LkEuNotificationListComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/notification-panel/notification-list/lk-eu-notification-list.component';
import { LkEuNotificationFavoriteListComponent } from '@zaoblako/web/modules/lk/external-user/dumbs/notification-panel/favorite-list/lk-eu-notification-favorite-list.component';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { SidebarModule } from 'primeng/sidebar';
import { TooltipModule } from 'primeng/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type LkEuInformerTabValue =
  | NotificationKindEnum.PERSONAL
  | NotificationKindEnum.HELPDESK
  | NotificationKindEnum.PASSAGE
  | 'FAVORITE';

interface InformerTypeOption {
  icon: string;
  label: string;
  value: LkEuInformerTabValue;
  visible: boolean;
}

@Component({
  selector: 'crm-lk-eu-notification-sidebar',
  host: { class: 'lk-eu-notification-sidebar' },
  standalone: true,
  imports: [
    CommonModule,
    SidebarModule,
    ButtonComponent,
    LkEuNotificationListComponent,
    LkEuNotificationFavoriteListComponent,
    TooltipModule
  ],
  templateUrl: './lk-eu-notification-sidebar.component.html',
  styleUrls: ['./lk-eu-notification-sidebar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LkEuNotificationSidebarComponent implements OnInit, OnDestroy {
  @Input() public position = 'right';

  @Output() public isShowChange = new EventEmitter<boolean>();

  /** Единственный источник правды для p-sidebar — без двусторонней привязки к родителю. */
  public sidebarVisible = false;

  /** Согласовано с PrimeNG transitionOptions (без дублирующего CSS transition). */
  public readonly sidebarTransitionOptions = '300ms cubic-bezier(0, 0, 0.2, 1)';

  public unreadCounts: Partial<Record<NotificationKindEnum, number>> = {};

  private readonly destroy$ = new Subject<void>();

  private openScheduled = false;

  @ViewChild(LkEuNotificationListComponent, { static: false })
  public notificationRef: LkEuNotificationListComponent;

  @ViewChild(LkEuNotificationFavoriteListComponent, { static: false })
  public notificationFavoriteRef: LkEuNotificationFavoriteListComponent;

  public informerTypeOptions: InformerTypeOption[] = [];

  private _selectedInformerType: LkEuInformerTabValue = NotificationKindEnum.PERSONAL;

  @Input()
  public set selectedInformerType(value: LkEuInformerTabValue) {
    this._selectedInformerType = value;
  }

  public get selectedInformerType(): LkEuInformerTabValue {
    return this._selectedInformerType;
  }

  public get NotificationKindEnum() {
    return NotificationKindEnum;
  }

  public get getInformerLabelByType(): string {
    return this.informerTypeOptions.find((option) => option.value === this.selectedInformerType)?.label;
  }

  public constructor(
    private readonly personalAccountMenuService: PersonalAccountMenuService,
    private readonly notificationService: NotificationService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    this.loadInformerTypesOptions();

    this.notificationService.unreadCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((counts) => {
        const next: Partial<Record<NotificationKindEnum, number>> = counts ?? {};
        const prev = this.unreadCounts;
        const unchanged =
          (prev[NotificationKindEnum.PERSONAL] ?? 0) === (next[NotificationKindEnum.PERSONAL] ?? 0) &&
          (prev[NotificationKindEnum.HELPDESK] ?? 0) === (next[NotificationKindEnum.HELPDESK] ?? 0) &&
          (prev[NotificationKindEnum.PASSAGE] ?? 0) === (next[NotificationKindEnum.PASSAGE] ?? 0);

        if (unchanged) {
          return;
        }

        this.unreadCounts = next;
        if (this.sidebarVisible) {
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Открытие только через вызов метода.
   * Откладываем на следующий кадр, чтобы не совпасть с CD layout и переносом appendTo в Firefox.
   */
  public open(): void {
    if (this.sidebarVisible || this.openScheduled) {
      return;
    }

    this.openScheduled = true;
    requestAnimationFrame(() => {
      this.openScheduled = false;

      if (this.sidebarVisible) {
        return;
      }

      this.sidebarVisible = true;
      this.changeDetectorRef.markForCheck();
    });
  }

  /**
   * PrimeNG при [visible]=true шлёт visibleChange(true) повторно — игнорируем.
   * Обрабатываем только закрытие (крестик / маска / swipe).
   */
  public onSidebarVisibleChange(visible: boolean): void {
    if (visible) {
      return;
    }

    if (!this.sidebarVisible) {
      return;
    }

    this.sidebarVisible = false;
    this.isShowChange.emit(false);
  }

  public closeSidebar(): void {
    this.onSidebarVisibleChange(false);
  }

  public selectInformerType(value: LkEuInformerTabValue): void {
    if (this._selectedInformerType === value) {
      return;
    }

    this._selectedInformerType = value;
    this.changeDetectorRef.markForCheck();
  }

  public refreshActiveList(): void {
    const kind = this.selectedInformerType;
    if (
      kind === NotificationKindEnum.PERSONAL ||
      kind === NotificationKindEnum.HELPDESK ||
      kind === NotificationKindEnum.PASSAGE
    ) {
      this.notificationRef?.loadNotifications();
      return;
    }

    this.notificationFavoriteRef?.loadNotifications();
  }

  public getUnreadCount(kind: LkEuInformerTabValue): number {
    if (kind === 'FAVORITE') {
      return 0;
    }

    return this.unreadCounts[kind] ?? 0;
  }

  private loadInformerTypesOptions(): void {
    this.personalAccountMenuService
      .getMenu()
      .subscribe({
        next: ({ items }) => {
          this.informerTypeOptions = [
            {
              icon: 'pi pi-user',
              label: 'Личные',
              value: NotificationKindEnum.PERSONAL,
              visible: true
            },
            {
              icon: 'pi pi-question-circle',
              label: 'Заявки',
              value: NotificationKindEnum.HELPDESK,
              visible: items?.HELPDESK?.enabled || items?.PARKING?.children?.TRANSIT_TASK?.enabled
            },
            {
              icon: 'pi pi-key',
              label: 'Пропуска',
              value: NotificationKindEnum.PASSAGE,
              visible: items?.PACS?.enabled
            },
            {
              icon: 'pi pi-star',
              label: 'Избранное',
              value: 'FAVORITE',
              visible: true
            }
          ];
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.informerTypeOptions = [];
          this.changeDetectorRef.markForCheck();
        }
      })
  }
}
